/**
 * MyDownloads.js — purchased digital albums
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from './ToastContainer';
import { isEmailVerified } from '../utils/emailVerified';
import LoadingSpinner from './LoadingSpinner';
import './MyDownloads.css';

const redownloadConfirmKey = (token) => `redownload_confirm_${token}`;

async function fetchAndSaveDownload(downloadUrl) {
  const res = await fetch(downloadUrl);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'Download failed');
  }
  const blob = await res.blob();
  const cd = res.headers.get('Content-Disposition');
  let filename = 'album-licensed.zip';
  const match = cd && /filename="([^"]+)"/.exec(cd);
  if (match) filename = match[1];
  const blobUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = blobUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(blobUrl);
}

const MyDownloads = () => {
  const { currentUser, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [downloads, setDownloads] = useState([]);
  const [pendingDownloads, setPendingDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const loadDownloads = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch('/api/digital/my-downloads', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to load downloads');
      const data = await res.json();
      if (Array.isArray(data)) {
        setDownloads(data);
        setPendingDownloads([]);
      } else {
        setDownloads(data.downloads || []);
        setPendingDownloads(data.pending || []);
      }
    } catch (err) {
      showError(err.message || 'Could not load downloads');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadDownloads();
  }, [loadDownloads]);

  useEffect(() => {
    const refreshOnReturn = () => {
      if (document.visibilityState === 'visible') loadDownloads();
    };
    document.addEventListener('visibilitychange', refreshOnReturn);
    return () => document.removeEventListener('visibilitychange', refreshOnReturn);
  }, [loadDownloads]);

  const downloadToken = searchParams.get('downloadToken');

  useEffect(() => {
    if (!downloadToken || authLoading) return;

    if (!currentUser) {
      const returnPath = `/profile?tab=downloads&downloadToken=${encodeURIComponent(downloadToken)}`;
      navigate(`/login?returnUrl=${encodeURIComponent(returnPath)}`, { replace: true });
      return undefined;
    }

    const storageKey = redownloadConfirmKey(downloadToken);
    const prior = sessionStorage.getItem(storageKey);
    if (prior === 'done' || prior === 'processing') return undefined;

    sessionStorage.setItem(storageKey, 'processing');
    let cancelled = false;

    const confirm = async () => {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch('/api/digital/confirm-redownload', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: downloadToken }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (cancelled) return;
          throw new Error(data.error || 'Confirmation failed');
        }
        sessionStorage.setItem(storageKey, 'done');
        if (data.downloadUrl) {
          await fetchAndSaveDownload(data.downloadUrl);
          if (!cancelled) {
            showSuccess('Download starting…');
            await loadDownloads();
          }
        }
      } catch (err) {
        if (cancelled) return;
        sessionStorage.removeItem(storageKey);
        showError(err.message || 'Could not confirm download');
      } finally {
        if (!cancelled) {
          searchParams.delete('downloadToken');
          setSearchParams(searchParams, { replace: true });
        }
      }
    };

    confirm();
    return () => {
      cancelled = true;
    };
    // Only `downloadToken`: toast/auth callbacks would re-run this effect and consume the token twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [downloadToken, authLoading, currentUser]);

  const handleDownload = async (entitlement) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    if (!isEmailVerified(currentUser)) {
      showError('Verify your email from Profile before downloading.');
      return;
    }

    setBusyId(entitlement.id);
    try {
      const res = await fetch('/api/digital/request-download', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ entitlementId: entitlement.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Download request failed');

      if (data.status === 'verification_sent') {
        showSuccess(data.message || 'Check your email to confirm this download.');
        return;
      }

      if (data.downloadUrl) {
        await fetchAndSaveDownload(data.downloadUrl);
        showSuccess('Download starting…');
        await loadDownloads();
      }
    } catch (err) {
      showError(err.message || 'Download failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading your downloads…" />;
  }

  if (!downloads.length && !pendingDownloads.length) {
    return (
      <div className="my-downloads-empty">
        <p>No digital downloads yet.</p>
        <p className="my-downloads-hint">
          Signed in as <strong>{currentUser?.email || 'unknown'}</strong>.
          Downloads appear here when a <strong>paid</strong> order includes a digital album product,
          the order is marked <strong>Completed</strong>, and you used this same email at checkout.
        </p>
        <p className="my-downloads-hint">
          While we review new orders, digital access stays locked until an admin marks your order complete.
          You will receive an email when your files are ready.
        </p>
        <p className="my-downloads-hint">
          Open the <strong>Orders</strong> tab to confirm your purchase, or buy a digital album while signed in.
        </p>
      </div>
    );
  }

  return (
    <div className="my-downloads-list">
      {pendingDownloads.map((item) => (
        <div key={`pending-${item.orderNumber}-${item.productId}`} className="my-downloads-card my-downloads-card-pending">
          <div className="my-downloads-card-body">
            <h4>{item.productName}</h4>
            <p className="my-downloads-meta">
              Order {item.orderNumber} · Status: {item.orderStatus || 'pending'}
            </p>
            <p className="my-downloads-desc">
              Payment received — download will unlock when this order is marked <strong>Completed</strong>.
            </p>
          </div>
          <button type="button" className="my-downloads-btn" disabled>
            Awaiting order completion
          </button>
        </div>
      ))}
      {downloads.map((item) => (
        <div key={item.id} className="my-downloads-card">
          <div className="my-downloads-card-body">
            <h4>{item.productName}</h4>
            {item.productDescription && (
              <p className="my-downloads-desc">{item.productDescription}</p>
            )}
            <p className="my-downloads-meta">
              Order {item.orderNumber}
              {item.downloadCount > 0 && ` · Downloaded ${item.downloadCount} time${item.downloadCount === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            className="my-downloads-btn"
            disabled={busyId === item.id}
            onClick={() => handleDownload(item)}
          >
            {busyId === item.id ? 'Preparing…' : item.downloadCount > 0 ? 'Download again' : 'Download ZIP'}
          </button>
        </div>
      ))}
    </div>
  );
};

export default MyDownloads;
